import React from "react";

import Stack from "@mui/material/Stack";

import EnumSelectorInput from "../../common/inputs/EnumSelectorInput";
import ToggleField from "../../common/inputs/ToggleField";
import UnitField from "../../common/inputs/UnitField";
import { fieldAccess } from "../../parametersProcessor/paramAccess";
import { IRailingParamTypeDescriptor } from '../../database/interfaces/paramScript/IParamTypeDescriptor';

/**
 * Railing (Keresztmerevítő) composite card: the CabinetFrame's back-bracing rail setup. Value is an
 * object {RailingType, RailingWidth} (e.g. {"RailingType":"Double","RailingWidth":"150mm"}), emitted
 * whole on every change — same copy-mutate-emit pattern as BackPassingCardContent.
 *
 * A be/ki állapot és a szélesség láthatósága ADAT-VEZÉRELT (a kártyában NINCS railing-típus beégetve):
 *   - `railingType.offValue` ("Fill" = teljes kitöltés): a kikapcsolt checkbox implicit értéke, ez nem
 *     választható opció. A checkbox akkor bekapcsolt, ha az érték ettől különbözik.
 *   - `railingType.onValue` ("Single"): erre váltunk bekapcsoláskor (a leggyakoribb eset, azonnal
 *     megjelenik a szélesség + a dekor) — utána a típus-választóból None/Double is választható.
 *   - `railingWidth.visibleForTypes` (["Single","Double"]): mely típusoknál értelmes a szélesség.
 * FIGYELEM: a Unity `UC.UberConfigurator.Elements.Containers.RailingType` enumja "Fill"-t ismer, "Full"-t
 * nem (lásd V47) — de ez már csak az ADATBAN van így, a kódban nem.
 */
interface IRailingCardContentProps {
    typeDescriptor: IRailingParamTypeDescriptor;
    paramValue: any;
    onUpdateParamValue: (value: any) => void;
}

const RailingCardContent: React.FC<IRailingCardContentProps> = ({ typeDescriptor, paramValue, onUpdateParamValue }) => {
    const d = typeDescriptor;
    // paramValue is the decoded object; may be "" before the value has loaded — fall back to defaults.
    const cur = (paramValue && typeof paramValue === "object") ? paramValue : {};

    // A be/ki állapot és a szélesség-mező láthatósága is ADATBÓL jön (nincs kódba írt railing-típus):
    //   - `railingType.offValue`: a kikapcsolt checkbox implicit értéke (a Unity enumja szerint "Fill"),
    //   - `railingType.onValue` : amire bekapcsoláskor váltunk (a leggyakoribb eset, "Single"),
    //   - `railingWidth.visibleForTypes`: mely típusoknál értelmes a szélesség.
    // Ha a descriptor nem adja meg őket, permisszív a viselkedés (bekapcsolva, mező látszik).
    const offValue = d.railingType?.offValue;
    const onValue = d.railingType?.onValue;

    const railingType: string = cur.RailingType ?? d.railingType?.default ?? offValue ?? "";
    const railingWidthStr: string = cur.RailingWidth ?? d.railingWidth?.default ?? "";

    const access = fieldAccess(d);

    const enabled = offValue === undefined || railingType !== offValue;

    // A szélesség KÜLÖN paraméterként (saját kártyában) él, hogy a panelen a sorrend Típus → Anyag →
    // Szélesség lehessen. Ez a kártya csak akkor rendereli (és csak akkor is emitálja a `RailingWidth`
    // mezőt), ha a descriptor MÉG tartalmazza a `railingWidth` blokkot — így egy nem migrált környezet
    // vagy másik termék viselkedése nem sérül.
    const hasInlineWidth = d.railingWidth !== undefined;
    const showWidth = hasInlineWidth && (d.railingWidth?.visibleForTypes?.includes(railingType) ?? true);

    const emit = (patch: { RailingType?: string; RailingWidth?: string }) => {
        onUpdateParamValue({
            RailingType: patch.RailingType ?? railingType,
            ...(hasInlineWidth ? { RailingWidth: patch.RailingWidth ?? railingWidthStr } : {}),
        });
    };

    return d ? (
        <Stack direction="column" spacing={1.5}>
            {/* `control="checkbox"`: ez a kártya eddig is Checkboxot használt (a többi kapcsoló Switch).
                Funkcionális oka nincs, de a váltás LÁTHATÓ változás lenne, ezért marad a mai. */}
            <ToggleField
                label={d.enabled?.label || "Kereszt merevítés"}
                checked={enabled}
                access={access}
                control="checkbox"
                onChange={(checked) => emit({
                    RailingType: (checked ? onValue : offValue) ?? railingType,
                })}
            />
            {enabled && (
                <EnumSelectorInput
                    label={d.railingType?.label}
                    value={railingType}
                    options={d.railingType?.options || {}}
                    tooltip={d.tooltip}
                    editable={access.editable}
                    readOnly={access.readOnly}
                    onValueChange={(v) => emit({ RailingType: v })}
                />
            )}
            {showWidth && (
                <UnitField
                    label={d.railingWidth?.label}
                    valueStr={railingWidthStr}
                    units={d.railingWidth?.units}
                    access={access}
                    unitChange="keep"
                    onChange={(v) => emit({ RailingWidth: v })}
                />
            )}
        </Stack>
    ) : null;
};

export default RailingCardContent;
